import AuthModal from "@/components/auth/AuthModal";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import {
  BarChart3,
  CircleUserRound,
  CreditCard,
  FileTextIcon,
  LayoutDashboard,
  LogOut,
  MailOpen,
  Menu,
  Search,
  Send,
  Settings,
  X,
  ArrowBigLeftDash,
} from "lucide-react";

import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { logoutUser } from "../../Redux/ReduxSlices/authSlice";
import { fetchUserDetails } from "../../Redux/ReduxSlices/profileSlice";
import MaxiPitchImage from "/images/maxipitch.png";

export default function Header() {
  const { user } = useAuth();
  // const { profile } = useAuth();

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const profile = useSelector((state) => state.profile.details);

  const isHome = location.pathname === "/";
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authModalTab, setAuthModalTab] = useState("signup");
  const [activeTab, setActiveTab] = useState("home");
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);
  const [showWaitingListModal, setShowWaitingListModal] = useState(false);

  useEffect(() => {
    // Call immediately on mount
    dispatch(fetchUserDetails());
  }, [dispatch]);

  useEffect(() => {
    const path = location.pathname;

    const routeTabMap = {
      "/": "home",
      "/home": "home",
      "/search": "search",
      "/dashboard": "dashboard",
      "/campaigns": "campaigns",
      "/email-template-manager": "emailtemplates",
      "/addemails": "addemail",
      "/send-emails": "sendemails",
      "/analytics": "analytics",
      "/settings": "settings",
    };

    const routes = Object.keys(routeTabMap).sort((a, b) => b.length - a.length);
    const exact = routes.find((route) => route === path);
    const starts = routes.find((route) => path.startsWith(route));
    const matchedTab = exact || starts;

    if (matchedTab) setActiveTab(routeTabMap[matchedTab]);
  }, [location]);

  const openAuthModal = (tab) => {
    setAuthModalTab(tab);
    setAuthModalOpen(true);
    setMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => setMobileMenuOpen((prev) => !prev);

  const handleTabChange = (value) => {
    setActiveTab(value);
    navigate(
      {
        home: "/",
        search: "/search",
        dashboard: "/dashboard",
        campaigns: "/campaigns",
        emailtemplates: "/email-template-manager",
        addemail: "/addemails",
        sendemails: "/send-emails",
        analytics: "/analytics",
      }[value],
    );
  };

  const navItems = [
    { label: "About Us", path: "/about-us" },
    { label: "Pricing", path: "/pricing-plans" },
    { label: "MaxiPitch Benefits", path: "/maxipitch-benefits" },
    { label: "How to Use", path: "/how-to-use" },
    { label: "FAQ", path: "/faq" },
  ];

  const logout = async () => {
    let response = await dispatch(logoutUser()).unwrap();
    // clearCampaignDraft();
    setLogoutModalOpen(false);
    toast.success("Logged out successfully");
    setTimeout(() => navigate("/"), 1500);
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-all duration-200 ${
        scrolled ? "bg-white/90 backdrop-blur-md shadow-sm" : "bg-white"
      }`}
    >
      <div
        className={` relative  flex items-center  max-md:h-16 h-12 px-4 py-8 sm:px-6 max-md:shadow-sm `}
      >
        {/* Logo */}
        <div className="flex items-center justify-center max-md:w-full max-md:justify-between gap-4">
          <img
            src={MaxiPitchImage}
            alt="MaxiPitch"
            className="h-7 max-md:h-6 cursor-pointer"
            onClick={() => navigate("/")}
          />
          {!user && (
            <div className="block md:hidden max-md:flex max-md:gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => openAuthModal("signup")}
              >
                Create Account
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => openAuthModal("login")}
              >
                Log in
              </Button>
            </div>
          )}

          {/* Desktop Navigation Tabs */}
          {user && (
            <div className="hidden lg:flex">
              <Tabs value={activeTab} onValueChange={handleTabChange}>
                <TabsList>
                  <TabsTrigger
                    value="search"
                    className="data-[state=active]:bg-white data-[state=active]:text-black"
                  >
                    <Search className="h-4 w-4 text-blue-600 group-hover:scale-110 transition-transform" />
                    <span className="px-2">Search</span>
                  </TabsTrigger>
                  {/* <TabsTrigger
                    value="addemail"
                    className="data-[state=active]:bg-white data-[state=active]:text-black"
                  >
                    <MailPlus className="h-4 w-4 text-emerald-600 group-hover:scale-110 transition-transform" />
                    <span className="px-2">Add Email</span>
                  </TabsTrigger> */}
                  <TabsTrigger
                    value="sendemails"
                    className="data-[state=active]:bg-white data-[state=active]:text-black"
                  >
                    <Send className="h-4 w-4 text-amber-600 group-hover:scale-110 transition-transform" />
                    <span className="px-2">Send Emails</span>
                  </TabsTrigger>
                  <TabsTrigger
                    value="emailtemplates"
                    className="data-[state=active]:bg-white data-[state=active]:text-black"
                  >
                    <FileTextIcon className="h-4 w-4 text-violet-600 group-hover:scale-110 transition-transform" />
                    <span className="px-2">Templates</span>
                  </TabsTrigger>

                  <TabsTrigger
                    value="campaigns"
                    className="data-[state=active]:bg-white data-[state=active]:text-black"
                  >
                    <MailOpen className="h-4 w-4 text-rose-600 group-hover:scale-110 transition-transform" />
                    <span className="px-2">Campaigns</span>
                  </TabsTrigger>
                  <TabsTrigger
                    value="analytics"
                    className="data-[state=active]:bg-white data-[state=active]:text-black"
                  >
                    <BarChart3 className="h-4 w-4 text-indigo-600 group-hover:scale-110 transition-transform" />{" "}
                    <span className="px-2"> Analytics</span>
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          )}

          {!user && (
            <div className="absolute left-1/2 -translate-x-1/2 hidden md:flex">
              <nav className="flex items-center gap-1">
                {navItems.map((item) => (
                  <button
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    className="
            px-4 py-1
            text-base font-medium text-gray-700
          
            transition-all duration-200
            hover:border-b-2 hover:border-indigo-500 hover:text-indigo-700
          "
                  >
                    {item.label}
                  </button>
                ))}
              </nav>
            </div>
          )}
        </div>

        {/* Right Section */}
        <div className="ml-auto flex items-center gap-3 max-md:gap-2">
          {user ? (
            <>
              {/* Credits (All viewports) */}
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-indigo-400 shadow-sm">
                <span className="text-sm md:text-base font-bold bg-gradient-to-r from-indigo-600 to-indigo-800 bg-clip-text text-transparent">
                  {profile?.data?.credit ?? 0}
                </span>
                <span className="text-xs md:text-sm font-medium text-indigo-900/80">
                  Credits
                </span>
              </div>

              {/* User Avatar Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="h-8 w-8 p-0 rounded-full">
                    <Avatar className="h-8 w-8 ">
                      <AvatarImage src={user?.avatar} alt="User" />
                      <AvatarFallback className="bg-gradient-to-br from-indigo-500 to-indigo-700 text-white font-semibold">
                        {(user?.email?.substring(0, 2) || "US").toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>

                <DropdownMenuContent
                  align="end"
                  className="w-56  bg-white/95 backdrop-blur-xl border border-indigo-100/50 shadow-xl shadow-indigo-500/10 rounded-xl overflow-hidden"
                >
                  <div className="px-3 flex items-center   py-2.5 bg-gradient-to-r from-indigo-500/10 to-transparent border-b border-indigo-100/30">
                    <CircleUserRound className="h-4 w-4 text-indigo-600" />
                    <DropdownMenuLabel className=" text-indigo-900/60     text-indigo-900 font-semibold truncate">
                      {user?.email}
                    </DropdownMenuLabel>
                  </div>
                  <DropdownMenuSeparator />

                  <DropdownMenuItem
                    asChild
                    className="cursor-pointer rounded-lg hover:bg-indigo-50/80 focus:bg-indigo-50 transition-colors"
                  >
                    <Link
                      to="/dashboard"
                      className="flex items-center gap-3 px-3 py-2.5"
                    >
                      <LayoutDashboard className="h-4 w-4 text-indigo-600" />
                      <span className="text-indigo-700 font-medium">
                        Dashboard
                      </span>
                    </Link>
                  </DropdownMenuItem>

                  <DropdownMenuItem
                    asChild
                    className="cursor-pointer rounded-lg hover:bg-indigo-50/80 focus:bg-indigo-50 transition-colors"
                  >
                    <Link
                      to="/settings"
                      className="flex items-center gap-3 px-3 py-2.5"
                    >
                      <Settings className="h-4 w-4 text-indigo-600" />
                      <span className="text-indigo-900/80 font-medium">
                        Account Settings
                      </span>
                    </Link>
                  </DropdownMenuItem>

                  <DropdownMenuItem
                    asChild
                    className="cursor-pointer rounded-lg hover:bg-indigo-50/80 focus:bg-indigo-50 transition-colors"
                  >
                    <Link
                      to="/pricing"
                      className="flex items-center gap-3 px-3 py-2.5"
                    >
                      <CreditCard className="h-4 w-4 text-indigo-600" />
                      <span className="text-indigo-900/80 font-medium">
                        Pricing
                      </span>
                    </Link>
                  </DropdownMenuItem>

                  <DropdownMenuSeparator />

                  <DropdownMenuItem
                    className="cursor-pointer rounded-lg text-red-600 hover:bg-indigo-50/80  "
                    onClick={() => setLogoutModalOpen(true)}
                  >
                    <div className="flex items-center gap-3 px-3 py-2.5">
                      <LogOut className="h-4 w-4" />
                      <span className="font-medium">Log out</span>
                    </div>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Mobile Menu Button */}
              <Button
                variant="ghost"
                size="sm"
                className="lg:hidden"
                onClick={toggleMobileMenu}
              >
                {mobileMenuOpen ? <X /> : <Menu />}
              </Button>
            </>
          ) : (
            <>
              {/* Mobile Menu Btn */}
              {/* <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                onClick={toggleMobileMenu}
              >
                {mobileMenuOpen ? <X /> : <Menu />}
              </Button> */}

              {/* Desktop Auth Buttons */}
              <div className="hidden md:flex items-center justify-between w-full">
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    className="border-[#2563eb] border-2"
                    size="sm"
                    onClick={() => openAuthModal("signup")}
                  >
                    Create Account
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-[#2563eb] border-2"
                    onClick={() => openAuthModal("login")}
                  >
                    Log in
                  </Button>

                  {/* <Button
                    size="sm"
                    className="main-button"
                    onClick={() => navigate("/contact-us")}
                  >
                    Let’s Talk
                  </Button> */}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Mobile Menu (Unauthenticated) */}
      {/* {mobileMenuOpen && !user && (
        <>
          <div
            className={`
      fixed inset-0 z-40 lg:hidden
      bg-gradient-to-br from-black/40 via-black/30 to-black/20
      backdrop-blur-sm transition-all duration-500
      ${mobileMenuOpen ? "opacity-100 visible" : "opacity-0 invisible"}
    `}
            onClick={toggleMobileMenu}
          />


          <div
            className={`
                                fixed top-0 right-0 h-full w-80 max-w-[90vw] z-50 lg:hidden
                                transform transition-all duration-500
                                ${mobileMenuOpen ? "translate-x-0" : "translate-x-full"}
                              `}
          >
            <div
              className="
                          h-full w-full
                          bg-white      
                          backdrop-blur-xl
                          shadow-2xl
                          border-l border-white/30
                          flex flex-col
                        "
            >
              <div className="flex items-center justify-between py-2 border-b border-indigo-100">
                <img
                  src={MaxiPitchImage}
                  alt="MaxiPitch"
                  className="h-5 cursor-pointer mx-4 bg-transparent"
                  onClick={toggleMobileMenu}
                />

                <button
                  onClick={toggleMobileMenu}
                  className="
                     mx-3 rounded-2xl
                    bg-gradient-to-br from-blue-500 to-purple-500
                    text-white
                    active:scale-95
                  "
                >
                  <ArrowBigLeftDash />
                </button>
              </div>

              <div className="flex-1 p-5 flex flex-col justify-start">
                <nav className="space-y-4">
                  <Link
                    to="/about-us"
                    onClick={toggleMobileMenu}
                    className="block px-4 py-2  hover:border-b-1 hover:border-indigo-700  text-gray-800"
                  >
                    About Us
                  </Link>

                  <Link
                    to="/pricing-plans"
                    onClick={toggleMobileMenu}
                    className="block px-4 py-2  hover:border-b-1 hover:border-indigo-700   text-gray-800"
                  >
                    Pricing
                  </Link>

                  <Link
                    to="/maxipitch-benefits"
                    onClick={toggleMobileMenu}
                    className="block px-4 py-2  hover:border-b-1 hover:border-indigo-700   text-gray-800"
                  >
                    Maxipitch Benifits
                  </Link>

                  <Link
                    to="/how-to-use"
                    onClick={toggleMobileMenu}
                    className="block px-4 py-2 hover:border-b-1 hover:border-indigo-700   text-gray-800"
                  >
                    How to Use
                  </Link>
                  <Link
                    to="/faq"
                    onClick={toggleMobileMenu}
                    className="block px-4 py-2 hover:border-b-1 hover:border-indigo-700   text-gray-800"
                  >
                    FAQ
                  </Link>
                </nav>
              </div>

              <div className="p-4 border-t border-gray-200">
                <div className="flex gap-4">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      toggleMobileMenu();
                      openAuthModal("signup");
                    }}
                  >
                    Create Account
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      toggleMobileMenu();
                      openAuthModal("login");
                    }}
                  >
                    Log in
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </>
      )} */}

      {mobileMenuOpen && user && (
        <div className="lg:hidden bg-gradient-to-br from-white via-gray-50 to-blue-50/50 border-t shadow-lg px-4 space-y-1 pb-4 animate-in slide-in-from-top-5 duration-500 backdrop-blur-sm">
          {/* Header with stepper indicator */}

          {/* Stepper-style navigation links */}
          <div className="space-y-4 pl-3 relative">
            {/* Vertical stepper line */}
            <div className="absolute left-[18px] top-2 bottom-2 w-0.5 bg-blue-600 "></div>

            {/* Menu Items with stepper indicators */}
            <div className="relative">
              <Link
                className="flex items-center gap-4 pl-4 pr-4 py-1  hover:bg-gradient-to-r hover:from-white hover:to-blue-50/80 hover:text-blue-700 transition-all duration-300 group active:scale-[0.99] shadow-sm hover:shadow-md bg-white/50 "
                to="/search"
              >
                <div className="relative">
                  <div className="absolute -inset-1 bg-blue-100 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="relative flex items-center justify-center w-8 h-8 rounded-full">
                    <Search className="h-4 w-4 text-blue-600 group-hover:scale-110 transition-transform" />
                  </div>
                </div>
                <div className="flex-1">
                  <span className="font-semibold text-gray-800 group-hover:text-blue-700">
                    Search
                  </span>
                </div>
              </Link>
            </div>

            {/* <div className="relative">
              <Link
                className="flex items-center gap-4 pl-4 pr-4 py-2  hover:bg-gradient-to-r hover:from-white hover:to-blue-50/80 hover:text-blue-700 transition-all duration-300 group active:scale-[0.99] shadow-sm hover:shadow-md bg-white/80 "
                to="/addemails"
              >
                <div className="relative">
                  <div className="relative flex items-center justify-center w-8 h-8 rounded-full ">
                    <MailPlus className="h-4 w-4 text-emerald-600 group-hover:scale-110 transition-transform" />
                  </div>
                </div>
                <div className="flex-1">
                  <span className="font-semibold text-gray-800 group-hover:text-blue-700">
                    Add Emails
                  </span>
                </div>
              </Link>
            </div> */}

            <div className="relative">
              <Link
                className="flex items-center gap-4 pl-4 pr-4 py-1  hover:bg-gradient-to-r hover:from-white hover:to-blue-50/80 hover:text-blue-700 transition-all duration-300 group active:scale-[0.99] shadow-sm hover:shadow-md bg-white/80 "
                to="/send-emails"
              >
                <div className="relative">
                  <div className="absolute -inset-1 bg-blue-100 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="relative flex items-center justify-center w-8 h-8 rounded-full ">
                    <Send className="h-4 w-4 text-amber-600 group-hover:scale-110 transition-transform" />
                  </div>
                </div>
                <div className="flex-1">
                  <span className="font-semibold text-gray-800 group-hover:text-blue-700">
                    Send Emails
                  </span>
                </div>
              </Link>
            </div>

            <div className="relative">
              <Link
                className="flex items-center gap-4 pl-4 pr-4 py-2  hover:bg-gradient-to-r hover:from-white hover:to-blue-50/80 hover:text-blue-700 transition-all duration-300 group active:scale-[0.99] shadow-sm hover:shadow-md bg-white/80 "
                to="/email-template-manager"
              >
                <div className="relative">
                  <div className="absolute -inset-1 bg-blue-100 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="relative flex items-center justify-center w-8 h-8 rounded-full ">
                    <FileTextIcon className="h-4 w-4 text-violet-600 group-hover:scale-110 transition-transform" />
                  </div>
                </div>
                <div className="flex-1">
                  <span className="font-semibold text-gray-800 group-hover:text-blue-700">
                    Templates
                  </span>
                </div>
              </Link>
            </div>

            <div className="relative">
              <Link
                className="flex items-center gap-4 pl-4 pr-4 py-1  hover:bg-gradient-to-r hover:from-white hover:to-blue-50/80 hover:text-blue-700 transition-all duration-300 group active:scale-[0.99] shadow-sm hover:shadow-md bg-white/80 "
                to="/campaigns"
              >
                <div className="relative">
                  <div className="absolute -inset-1 bg-blue-100 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="relative flex items-center justify-center w-8 h-8 rounded-full ">
                    <MailOpen className="h-4 w-4 text-rose-600 group-hover:scale-110 transition-transform" />
                  </div>
                </div>
                <div className="flex-1">
                  <span className="font-semibold text-gray-800 group-hover:text-blue-700">
                    Campaigns
                  </span>
                </div>
              </Link>
            </div>

            <div className="relative space-y-4">
              <Link
                className="flex items-center gap-4 pl-4 pr-4 py-1 hover:bg-gradient-to-r hover:from-white hover:to-blue-50/80 hover:text-blue-700 transition-all duration-300 group active:scale-[0.99] shadow-sm hover:shadow-md bg-white/80 "
                to="/analytics"
              >
                <div className="relative">
                  <div className="absolute -inset-1 bg-blue-100 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="relative flex items-center justify-center w-8 h-8 rounded-full ">
                    <BarChart3 className="h-4 w-4 text-indigo-600 group-hover:scale-110 transition-transform" />
                  </div>
                </div>
                <div className="flex-1">
                  <span className="font-semibold text-gray-800 group-hover:text-blue-700">
                    Analytics
                  </span>
                </div>
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* AUTH MODAL */}
      <AuthModal
        isOpen={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        defaultTab={authModalTab}
      />

      {/* LOGOUT CONFIRMATION */}
      <Dialog open={logoutModalOpen} onOpenChange={setLogoutModalOpen}>
        <DialogContent
          className="
        text-center
        w-[calc(100%-2rem)]
        max-w-lg
        sm:w-full
        sm:max-w-lg
   
        bg-gradient-to-br from-white to-indigo-50
        shadow-xl
        rounded-xl
        px-6 py-7
      "
        >
          <div className="absolute top-0 right-0 h-full w-1 bg-indigo-600 rounded-tr-xl rounded-br-xl" />

          <DialogHeader className="space-y-2">
            <DialogTitle className="text-xl font-semibold text-gray-900">
              Confirm Logout
            </DialogTitle>
            <DialogDescription className="text-sm text-gray-600">
              Are you sure you want to log out? You’ll need to sign in again to
              continue.
            </DialogDescription>
          </DialogHeader>

          <div className="flex gap-4 justify-center mt-6">
            <Button
              onClick={logout}
              className="main-button hover:bg-indigo-700 text-white px-6"
            >
              Log out
            </Button>

            <Button
              variant="outline"
              onClick={() => setLogoutModalOpen(false)}
              className="px-6 border-gray-300"
            >
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

     
    </header>
  );
}
